import {
  Button,
  OutlinedSelect,
  SearchBox,
  useMobile,
  SelectOptionType,
} from '@octano/global-ui';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { useStudentFileFilterLoader } from './FormLoader';

interface StudenFileSearchFiltersType {
  nameOrId?: string;
  campusId?: SelectOptionType & { value: number };
  schoolId?: SelectOptionType & { value: number };
  studyPlanId?: SelectOptionType & { value: number };
}

export default function SearchFilters(props: {
  onSearch: (data: {
    campusId?: number;
    schoolId?: number;
    studyPlanId?: number;
    nameOrId?: string;
  }) => void;
}) {
  const { onSearch } = props;
  const { t } = useTranslation(TranslationsKeys.STUDENT_FILE);
  const { data } = useStudentFileFilterLoader();
  const { campuses, schools, studyPlans } = data!;
  const options = useMemo(
    () => ({
      campuses: campuses.map((item) => ({
        label: item.name,
        value: item.id,
      })),
      schools: schools.map((item) => ({
        label: item.name,
        value: item.id,
      })),
      studyPlan: studyPlans.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    }),
    [campuses, schools, studyPlans],
  );

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm<StudenFileSearchFiltersType>();
  const currentFiltersValues = watch();
  const isMobile = useMobile();

  const onSubmit = (values: StudenFileSearchFiltersType) => {
    onSearch({
      campusId: values.campusId?.value,
      schoolId: values.schoolId?.value,
      studyPlanId: values.studyPlanId?.value,
      nameOrId: values.nameOrId,
    });
  };

  const cleanFilters = () => {
    const needToClean = Object.values(currentFiltersValues).some(
      (value) => value !== null,
    );
    if (needToClean) {
      reset();
      onSearch({
        campusId: undefined,
        schoolId: undefined,
        studyPlanId: undefined,
        nameOrId: undefined,
      });
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row className="pb-4 gutters-filters">
        <Col xs={12} md={4}>
          <SearchBox
            name="nameOrId"
            label={t(`filters.nameOrId`)}
            placeholder={t(`filters.nameOrIdPlaceholder`)}
            control={control}
            clear={() => setValue('nameOrId', '')}
          />
        </Col>
        <Col xs={12} md={4}>
          <OutlinedSelect
            name="studyPlanId"
            options={options.studyPlan}
            label={t(`filters.studyPlan`)}
            placeholder={t(`filters.studyPlanPlaceholder`)}
            control={control}
            isClearable
          />
        </Col>
        <Col xs={12} md={4}>
          <OutlinedSelect
            name="schoolId"
            options={options.schools}
            label={t(`filters.school`)}
            placeholder={t(`filters.schoolPlaceholder`)}
            control={control}
            isClearable
          />
        </Col>
        <Col xs={12} md={4}>
          <OutlinedSelect
            name="campusId"
            options={options.campuses}
            label={t(`filters.campus`)}
            placeholder={t(`filters.campusPlaceholder`)}
            control={control}
            isClearable
          />
        </Col>
        <Col>
          <div className="float-right">
            <Button
              type="submit"
              text={t('filters.search')}
              size="sm"
              style={{
                width: isMobile ? '100%' : 125,
                height: 41,
                marginTop: isMobile ? 0 : '1.5rem',
              }}
            />
            <Button
              text={t('filters.clean')}
              size="sm"
              outlined
              onClick={cleanFilters}
              style={{
                width: isMobile ? '100%' : 125,
                height: 41,
                marginTop: isMobile ? '1rem' : '1.5rem',
                marginLeft: 10,
              }}
            />
          </div>
        </Col>
      </Row>
    </Form>
  );
}
